/**
 * The B2B "Get In Touch" enquiry log — `GET /b2b-proposal/submissions`.
 *
 * ══ 🔴 WHAT THIS PANEL MAY SAY, AND THE ONE THING IT MAY NEVER SAY ═════════
 *
 * 🔴 **A FAILED READ PRINTS NO COUNT.** `rows === null` means WE COULD NOT ASK;
 * `[]` means we asked and there are none. Two sibling screens shipped the
 * opposite on 2026-09-20 — `0 service mappings` printed **and announced through
 * a polite live region**, 31px above the banner saying the read had failed —
 * and the cause was one `??` on a field whose job is to report a gap. So:
 *
 *   - the count renders **only** while {@link rows} is an array;
 *   - the honest zero SURVIVES, because `[]` is a real answer;
 *   - and the count sits in **no live region at all**. The sibling defect was
 *     spoken as well as printed, and a `role="status"` on a figure that can be
 *     absent is how the spoken half comes back. The error block carries
 *     `role="alert"`, which is the thing worth announcing.
 *
 * ⚠️ **`loading` WITH NO ROWS YET IS ALSO NOT A COUNT** — it draws a skeleton in
 * the count's place rather than a `0`, because a skeleton is not a claim.
 *
 * ══ 🔴 THE ROW ACTIONS ARE CALLBACKS — AND THE REASON HAS CHANGED ══════════
 *
 * ⚠️ *SUPERSEDED 2026-09-20, the PREMISE and not the seam: "**Measured in
 * `emerge-backend-app` on 2026-09-20 … the module publishes THREE routes and no
 * more** — `POST /submissions`, `GET /submissions`, `GET /submissions/:id`.
 * There is **no archive route, no unarchive route, no delete route and no
 * attach-a-deal route**, and no column for any of them … the router says in
 * terms that a `DELETE` is an **UNMADE OWNER DECISION** (contract §3.3)."*
 *
 * **Re-measured the same day in the same file: the module publishes SIX** —
 * `POST /submissions`, `GET /submissions`, **`PATCH /submissions/:id/archive`**,
 * **`PATCH /submissions/:id/deal`**, `GET /submissions/:id` and
 * **`DELETE /submissions/:id`**. The owner took the decision the router recorded
 * as unmade, and migration 232 carries the columns. ⚠️ **Archive and unarchive
 * are ONE route**, which is why nothing here should read the pair as two.
 *
 * ✅ **THE SEAM SURVIVES ITS OWN JUSTIFICATION, WHICH IS WHY IT IS CORRECTED
 * RATHER THAN DELETED.** Each action is still an optional callback and an action
 * with no callback is still WITHHELD rather than rendered inert — **not because
 * no route exists, but because `api.ts` and the page that mounts this panel are
 * owned elsewhere**, and because the panel must own every GATE so the table and
 * the phone card cannot disagree about who may destroy a record.
 *
 * ⛔ **RIGHT ANSWER, DEAD REASON, NOTHING FAILING** is the shape this repo
 * records against the tax-control passage: a reader who checks the premise finds
 * it false and concludes the seam should be torn out for a route that now
 * exists. The seam is not what was wrong; the sentence under it was.
 *
 * ══ ✅ THE TWO VIEW-MODEL FIELDS ARE CLOSED ════════════════════════════════
 *
 * ⚠️ *SUPERSEDED 2026-09-20: "{@link archivedOf} and {@link linkedDealOf} answer
 * `false` and `null` for every row today, **and they are named functions rather
 * than literals so the question has one place to be answered when a field
 * arrives** … ⚠️ **THE CONSEQUENCE, STATED RATHER THAN DISCOVERED: with
 * `archivedOf` false everywhere, Archive is offerable on every row and Delete is
 * offerable on none** — delete requires an already-archived row. That is the
 * safe direction to be wrong in: the withheld control is the destructive one."*
 *
 * **Migration 232 landed both columns and both fields are on
 * `B2bProposalSubmission`**, so each function reads the real field and carries
 * its own `CLOSED` note. ⛔ **A stub that outlives its measurement is not inert
 * — it is a wrong answer with a reason attached**, and the consequence quoted
 * above WAS the live behaviour: an archived enquiry rendered as live and was
 * offered `Archive` again, while `Delete permanently` could be reached by
 * nobody. The named-function half of that paragraph is the half that paid off:
 * the question had one place to be answered, and it was answered there.
 *
 * ⛔ **THE PROHIBITION THE OLD BLOCK CARRIED IS NOT RETIRED WITH IT.** Neither
 * may be closed by reading a key off the response that is not on the type: a key
 * a client declares and a server never sends reads back `undefined` for ever,
 * renders blank, and nothing fails. A cast through `{ archivedAt?: string }` is
 * that defect with a cast in front of it.
 *
 * ══ 🔴 THREE VIEWS, AND THE THIRD IS SPELLED BY AN ABSENT KEY ══════════════
 *
 * **Live / Archived / All**, opening on **Live** — {@link PROPOSAL_VIEWS}. They
 * map onto the server's tri-state `archived` filter exactly:
 *
 * | pill | what is sent |
 * | --- | --- |
 * | **Live** | `archived: false` — the working queue |
 * | **Archived** | `archived: true` — the archive itself, and nothing live |
 * | **All** | 🔴 **the key OMITTED.** Absent means everything; there is no third value to send |
 *
 * 🔴 **THE FILTER AND THE PILLS ARE ONE CHANGE, NOT TWO.** `Unarchive` and
 * `Delete permanently` both require `row.archived`, so a panel that started
 * sending `archived: false` and nothing else would make **both controls
 * unreachable** — the destructive one silently, on a screen still drawing its
 * confirmation dialog for a row it can no longer show. ⛔ **That is this
 * codebase's worst-recorded UI failure mode**: a working control reported as
 * DELETED when it had merely become unreachable.
 *
 * ⛔ **`archived: false` MUST ACTUALLY SERIALISE, AND IT DOES BY MEASUREMENT.**
 * `listQuery` skips `undefined`, `null` and `''` — **not falsy values** — so
 * `String(false)` reaches the wire as `?archived=false`. A "drop the falsy
 * params" tidy-up there turns the working queue into the whole log, with a `200`
 * and a plausible list, which is the one outcome this key exists to prevent.
 *
 * ⚠️ **THE VIEW IS LOCAL STATE AND NOT A URL KEY, DELIBERATELY.** Every other
 * pill bank in this app (`/operations`, `CommissionsPage`, `/settings`) stores
 * its choice in `?tab=` — but this panel is a SECTION of a page that already
 * owns that key for its four sections, and a second key would have to join that
 * page's `CLEARED_ON_SWITCH` list to avoid narrowing a sibling section. **That
 * file is owned elsewhere**, and half of a two-file rule is worse than neither
 * half. ⚠️ **The cost, stated: the view is not shareable and does not survive a
 * reload.**
 *
 * ══ ⛔ NO HELPER TEXT ══════════════════════════════════════════════════════
 *
 * No `hint`, no `PanelHeader description`, no sentence under a control. The
 * sentences that ARE here are the four kinds the rule keeps: the error block,
 * the refusal, the `EmptyState` descriptions, and the `ConfirmDialog` body
 * naming a destructive consequence. Every `title` here carries a fact about the
 * data a cell cannot show — the documented exception — never advice.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  ENQUIRY_ACTIONS_COLUMN_WIDTH,
  EnquiryRowActions,
  type EnquiryActionItem,
} from '@/components/enquiries/EnquiryRowActions';
import {
  Badge,
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  FilterableTH,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type ColumnFilterOption,
  type FrozenSide,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { EnquiryAssignmentCell } from '@/components/enquiries/EnquiryAssignmentCell';
import {
  ASSIGNEE_PARAM,
  ENQUIRY_ASSIGNMENT_COLUMN_WIDTH,
  assigneeFilterOf,
  assignmentColumnSpec,
} from '@/components/enquiries/enquiry-assignment-filter';
import { ApiError, api } from '@/lib/api';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { formatDateTime, orDash, pluralise } from '@/lib/format';
import type {
  B2bProposalListParams,
  B2bProposalSubmission,
  EnquiryAssignment,
} from '@/lib/types';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec, type TableFilters } from '@/lib/use-table-filters';
/*
 * ⚠️ **THE GATE IS IMPORTED, NEVER RE-DERIVED.** The list that offers `Convert`
 * and the flow that runs it must not be able to disagree about whether an
 * enquiry has already been converted — they did, and the button appeared on
 * rows the flow refused. `ProposalConvertFlow` does not import this file, so
 * there is no cycle.
 */
import { proposalIsConvertible } from './ProposalConvertFlow';
import { useAuth } from '@/lib/use-auth';
import { EventInterestIcon } from './icons';

/* -------------------------------------------------------------------------- */
/* The view model                                                             */
/* -------------------------------------------------------------------------- */

/** One service ticked on the enquiry, as a cell renders it. */
export interface ProposalService {
  /** Stable render key: the sender's slug is unique within one submission. */
  key: string;
  /** The sender's own slug — **always rendered where {@link name} is null.** */
  sourceKey: string;
  /**
   * The EMERGE product this key means, or `null` where nobody has mapped it.
   *
   * ⛔ **NEVER COALESCED TO A WORD.** `?? 'Unknown'` invents a product and hides
   * an administrator's missing configuration, which is the one thing this field
   * exists to report. The mapping table ships EMPTY, so on day one every
   * service on every row is `null` — **the ordinary state, not a fault.**
   */
  name: string | null;
}

/**
 * One enquiry, as this panel renders it.
 *
 * ⚠️ **A VIEW MODEL, which is the rule the whole of
 * `src/components/event-interest/` runs on** — every wire field name is spelled
 * once, in {@link rowOf}, so two structurally identical fields cannot be
 * swapped by a renderer with nothing to flag it.
 */
export interface ProposalRow {
  id: number;
  /** Free text the visitor typed. ⛔ Never matched, joined or deduplicated on. */
  institutionName: string;
  /**
   * The company somebody attached **by hand**, or `null`.
   *
   * 🔴 **`null` IS THE ORDINARY STATE — every enquiry arrives unlinked.** ⛔ So
   * nothing here words it as a fault, nags about it, or renders it as a gap.
   */
  companyName: string | null;
  /** The attached company's id, for the link in the actions lane. `null` with {@link companyName}. */
  companyId: number | null;
  /**
   * Where the institution is, composed once in {@link rowOf} — e.g.
   * `Nigeria (NG) · Africa` — or `null` when none of the three is recorded.
   * ⛔ `null` renders as the house empty glyph, never an invented word.
   */
  location: string | null;
  contactName: string;
  contactEmail: string;
  contactDesignation: string | null;
  contactPhone: string | null;
  /** Newline-bearing, capped at 16 000 characters. Rendered as TEXT, never markup. */
  message: string;
  services: readonly ProposalService[];
  /** The SENDER's stamp — when the visitor submitted, not when EMERGE stored it. */
  submittedAt: string;
  /** The only string tying this row to a row in the sender's database. */
  sourceReference: string;
  /** See {@link archivedOf}. */
  archived: boolean;
  /**
   * The deal this enquiry became, or `null`.
   *
   * ⛔ **NOT THE CONVERT GATE — read {@link convertible} for that.** `null` here
   * is two facts (never converted; converted and the deal has since been
   * deleted), and gating on it re-opens conversion the moment somebody deletes
   * a deal. It is carried to WORD the row, not to decide it.
   */
  dealId: number | null;
  /**
   * When this enquiry was FIRST converted, or `null` — the discriminator that
   * makes {@link dealId} readable. Not cleared when the deal is deleted.
   */
  convertedAt: string | null;
  /**
   * 🔴 **THE GATE, COMPUTED ONCE FROM THE SHARED PREDICATE.**
   *
   * ⚠️ **It is a row field rather than a test at the render site because the
   * list that OFFERS the control and the flow that RUNS it must not be able to
   * disagree.** They did: this panel read `row.dealId === null` while
   * `proposalIsConvertible` read the pair, so the button appeared on rows the
   * flow then refused. One predicate, one answer, spelled in {@link rowOf}.
   */
  convertible: boolean;
  /**
   * `true` only when the server REPORTS no consent record — a historical import
   * from before the website captured consent (owner decision 2026-09-26).
   *
   * ⛔ **`=== false`, NEVER `!consentRecorded` or `?? false`.** An older server
   * does not send the key, and `undefined` means *not reported*, which must show
   * nothing rather than mark every enquiry as lacking consent.
   */
  noConsentRecorded: boolean;
  /**
   * The campaign (UTM) tags the enquiry arrived with, in a fixed order —
   * source, medium, campaign — and only the ones that carry a value.
   *
   * ⚠️ **`[]` is the ordinary state and is NOT a claim about how the visitor
   * arrived.** It means no tag was sent; nothing here words it as "Direct" or
   * "Unknown", because either would be a label nobody recorded.
   */
  campaignTags: readonly CampaignTag[];
  /**
   * The latest assignment. `null` is unassigned; `undefined` is an older server
   * that does not report the field — rendered as a dash, never "Unassigned".
   */
  assignment: EnquiryAssignment | null | undefined;
}

/** One UTM tag that carried a value. `kind` is rendered as real text beside it. */
export interface CampaignTag {
  kind: 'source' | 'medium' | 'campaign';
  value: string;
}

/**
 * ⛔ **`typeof x === 'string'`, NEVER `!== null`** — this app can deploy ahead of
 * its API, and an absent key reads `undefined`. A blank string is not a tag.
 */
function campaignTagsOf(submission: B2bProposalSubmission): readonly CampaignTag[] {
  const candidates: readonly [CampaignTag['kind'], unknown][] = [
    ['source', submission.utmSource],
    ['medium', submission.utmMedium],
    ['campaign', submission.utmCampaign],
  ];
  const tags: CampaignTag[] = [];
  for (const [kind, raw] of candidates) {
    if (typeof raw === 'string' && raw.trim() !== '') tags.push({ kind, value: raw.trim() });
  }
  return tags;
}

/**
 * ✅ **CLOSED 2026-09-20, BY THE EXIT CONDITION THIS COMMENT NAMED.**
 * ⚠️ *SUPERSEDED: "**NOTHING ON THE WIRE RECORDS WHETHER AN ENQUIRY IS
 * ARCHIVED**, measured in the backend module on 2026-09-20: no route, no
 * column, no key on `B2bProposalSubmission`. ⛔ **Do not close this with a
 * cast.** The event that closes it is an `archivedAt` (or equivalent) field
 * appearing on `B2bProposalSubmission` — not a deploy, not a message, and not
 * this comment."*
 *
 * **Migration 232 added the column and the field is on the type.** The stub
 * answered `false` for every row, so an archived enquiry rendered as live and
 * was offered `Archive` again — ⛔ **a stub that outlives its measurement is
 * not inert, it is a wrong answer with a reason attached.**
 *
 * ⛔ **`typeof x === 'string'`, NEVER `!== null`.** The key is optional because
 * this app can deploy ahead of its API, and an absent key reads `undefined` —
 * `undefined !== null` is **true**, which would mark every row on an older
 * server as archived and empty the default view.
 */
function archivedOf(submission: B2bProposalSubmission): boolean {
  return typeof submission.archivedAt === 'string';
}

/**
 * ✅ **CLOSED 2026-09-20, on a decision that was taken rather than inferred.**
 * ⚠️ *SUPERSEDED: "**AND NOTHING RECORDS A DEAL EITHER, DELIBERATELY.** The
 * contract's own ruling (§1.2) is that **a proposal is an unlinked enquiry and
 * no commercial record follows from it** … a deal link would be a new field on
 * a new decision."* **That decision was made** — the owner chose *"Link it to
 * the deal, show it, block a second convert"* — and migration 232 carries the
 * column.
 *
 * 🔴 **THE STUB MADE THE BLOCK INERT.** Answering `null` for every row meant
 * `Convert to a deal` was offered on **every** enquiry including converted
 * ones, so the one thing owner decision 4 asked for did not happen in the list
 * at all. The page's own re-check and the server's `409` were the only things
 * behind it.
 *
 * ⛔ **`typeof x === 'number'`, NEVER `!== null`** — same reason as
 * {@link archivedOf}, and this exact test shipped *"Raised Invalid Date, and
 * since deleted"* on the event-interest twin.
 */
function linkedDealOf(submission: B2bProposalSubmission): number | null {
  return typeof submission.dealId === 'number' ? submission.dealId : null;
}

/**
 * `Nigeria (NG) · Africa`, from the three location fields, or `null` when none
 * carries a value.
 *
 * ⛔ **`typeof x === 'string'`, NEVER `!== null`** — same reason as
 * {@link archivedOf}: an older API does not send the keys, and `undefined`
 * must read as *not recorded*. Each part renders only when present, so a
 * missing code or continent drops out rather than printing a placeholder word.
 */
function locationOf(submission: B2bProposalSubmission): string | null {
  const text = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
  const country = text(submission.institutionCountry);
  const code = text(submission.institutionCountryCode);
  const continent = text(submission.institutionContinent);
  const geography = geographyLabel(text(submission.geographyOfFocus));
  const place =
    country !== null && code !== null ? `${country} (${code})` : (country ?? code);
  const parts = [place, continent, geography === null ? null : `Focus: ${geography}`].filter(
    (part): part is string => part !== null,
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * The geography slug as a reader sees it — `south-east-asia` → `South East
 * Asia`, `mena` → `MENA`. Display only; routing matches the stored slug.
 */
function geographyLabel(slug: string | null): string | null {
  if (slug === null) return null;
  if (slug.toLowerCase() === 'mena') return 'MENA';
  return slug
    .split('-')
    .filter((word) => word !== '')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * The one place a wire field name is spelled.
 *
 * ⚠️ **`company` IS READ WITH `?.` AND NEVER DEREFERENCED.** Its presence on the
 * response is unconfirmed by the contract, so three states reach us — a
 * company, an explicit `null`, and the key absent — and the accessor collapses
 * only the two that mean *there is no company to show*.
 */
function rowOf(submission: B2bProposalSubmission): ProposalRow {
  return {
    id: submission.id,
    institutionName: submission.institutionName,
    companyName: submission.company?.name ?? null,
    companyId: typeof submission.company?.id === 'number' ? submission.company.id : null,
    location: locationOf(submission),
    contactName: submission.contactName,
    contactEmail: submission.contactEmail,
    contactDesignation: submission.contactDesignation,
    contactPhone: submission.contactPhone,
    message: submission.message,
    services: submission.services.map((service, index) => ({
      /* The sender may legitimately repeat a slug; the index keeps the key
         stable without pretending the slug is unique. */
      key: `${service.key}:${index}`,
      sourceKey: service.key,
      name: service.name,
    })),
    submittedAt: submission.submittedAt,
    sourceReference: submission.sourceReference,
    archived: archivedOf(submission),
    dealId: linkedDealOf(submission),
    convertedAt: typeof submission.convertedAt === 'string' ? submission.convertedAt : null,
    convertible: proposalIsConvertible(submission),
    noConsentRecorded: submission.consentRecorded === false,
    campaignTags: campaignTagsOf(submission),
    assignment: submission.assignment,
  };
}

/* -------------------------------------------------------------------------- */
/* The three views                                                            */
/* -------------------------------------------------------------------------- */

/** Which population the list is showing. See the file header for the mapping. */
export type ProposalView = 'live' | 'archived' | 'all';

/**
 * 🔴 **ONE ROW PER VIEW, CARRYING THE PILL LABEL *AND* THE COUNT'S NOUN.**
 *
 * The heading's figure has to name the population it describes or it is a
 * confident claim about the wrong set — *"20 enquiries"* over the archive says
 * twenty enquiries exist when twenty are merely filed away. Spelling the noun
 * here rather than at the heading is what stops the pill and the count drifting
 * into describing two different things.
 *
 * ⚠️ **`all` uses the BARE noun, not "all enquiries".** The count is already the
 * whole log; *"20 all enquiries"* is not English, and *"all 20 enquiries"* would
 * claim completeness the pager contradicts on page 1 of 4.
 */
interface ProposalViewSpec {
  value: ProposalView;
  label: string;
  /** Singular noun for {@link pluralise} — "1 live enquiry". */
  singular: string;
  /** Plural noun — "20 live enquiries". */
  plural: string;
  /** The empty state's title, which must not claim more than the view can see. */
  emptyTitle: string;
  /** The empty state's description. ⛔ Never implies data is on its way. */
  emptyDescription: string;
}

/**
 * ⚠️ **NAMED SO {@link viewSpecOf} HAS A FALLBACK THAT IS NOT AN ASSERTION.**
 * `noUncheckedIndexedAccess` is on, so `PROPOSAL_VIEWS[0]` is `| undefined` and
 * a `!` would be a claim `tsc` cannot check. A named constant is the same
 * default stated in a form the compiler can see.
 */
const LIVE_VIEW: ProposalViewSpec = {
  value: 'live',
  label: 'Live',
  singular: 'live enquiry',
  plural: 'live enquiries',
  emptyTitle: 'No live enquiries',
  /*
   * ⚠️ **IT NAMES THE ARCHIVE, BECAUSE AN EMPTY WORKING QUEUE HAS TWO CAUSES
   * AND ONLY ONE OF THEM IS *nobody has enquired*.** A reader who has just
   * archived the last row must not be told the log is empty.
   */
  emptyDescription:
    'Nothing is waiting here. Archived enquiries are on the Archived view, and new ones appear as institutions send the Get In Touch form.',
};

const PROPOSAL_VIEWS: readonly ProposalViewSpec[] = [
  LIVE_VIEW,
  {
    value: 'archived',
    label: 'Archived',
    singular: 'archived enquiry',
    plural: 'archived enquiries',
    emptyTitle: 'Nothing is archived',
    /*
     * ⛔ **THIS MUST NOT READ AS *no enquiries exist*.** An empty archive over a
     * full log is the ordinary state, and the sentence says which fact it is
     * reporting rather than leaving the reader to infer the other one.
     */
    emptyDescription: 'No enquiry has been archived. Archiving one takes it off the Live view.',
  },
  {
    value: 'all',
    label: 'All',
    singular: 'enquiry',
    plural: 'enquiries',
    emptyTitle: 'No enquiries yet',
    emptyDescription:
      'Enquiries appear here as institutions send the Get In Touch form on the marketing site.',
  },
];

/** ⚠️ Read off the spec rather than spelled again, so the two cannot disagree. */
const DEFAULT_VIEW: ProposalView = LIVE_VIEW.value;

/**
 * ⚠️ **THE FALLBACK IS UNREACHABLE AND IS NOT DEFENSIVE PADDING.**
 * `ProposalView` is a closed union and every member has a row, so `find` always
 * hits — but `Array.prototype.find` is typed `| undefined` whatever the data,
 * and the alternative is a non-null assertion, which asserts exactly the thing
 * the compiler is declining to believe.
 */
function viewSpecOf(view: ProposalView): ProposalViewSpec {
  return PROPOSAL_VIEWS.find((spec) => spec.value === view) ?? LIVE_VIEW;
}

/**
 * 🔴 **THE TRI-STATE, SPELLED ONCE.**
 *
 * ⛔ **`all` RETURNS AN EMPTY OBJECT RATHER THAN `{ archived: undefined }`.**
 * They behave identically today — `listQuery` skips `undefined`, and
 * `exactOptionalPropertyTypes` is off so both typecheck — but **both halves of
 * that are properties somebody can change**, and the contract's own note says
 * to prefer omitting the key. A spread of `{}` cannot be separated from an
 * absent key by either change.
 */
function archivedParamOf(view: ProposalView): { archived?: boolean } {
  if (view === 'all') return {};
  return { archived: view === 'archived' };
}

/**
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`** — copied from
 * `CommissionsPage`'s `TabToggle` and `SettingsPage`'s `SettingsTabs` rather
 * than re-derived, because this app already answered the question: these switch
 * which population the list below IS, and a real `tablist` would promise
 * arrow-key navigation between panels that do not share a tabstop.
 *
 * ⚠️ **Every pill renders at every width, always**, and the selected one is not
 * distinguished by colour alone: `secondary` gives it a raised surface against
 * `ghost`'s transparent one, and `aria-pressed` states it outright.
 */
function ViewToggle({
  value,
  onChange,
  disabled,
}: {
  value: ProposalView;
  onChange: (next: ProposalView) => void;
  /** A read is in flight; a second would race it. ⛔ Never hides a pill. */
  disabled: boolean;
}) {
  return (
    <div
      role="group"
      aria-label="Enquiries shown"
      /*
       * 🔴 **`max-w-full` AND NO `shrink-0` — THE `SettingsPage` CLASS STRING,
       * NOT THE `CommissionsPage` ONE, AND THE DIFFERENCE IS MEASURED RATHER
       * THAN STYLISTIC.**
       *
       * That toggle carries `shrink-0` and is right to: it holds TWO pills and
       * shares a row with other controls, so it must not be squeezed. **On a
       * flex item `shrink-0` means the box never becomes narrow enough for its
       * own `flex-wrap` to fire**, so the children stay on one row and the last
       * pill is CLIPPED by an ancestor rather than wrapped — present in the DOM,
       * off the viewport, reachable by nothing. `/settings` shipped exactly that
       * with four pills at 320 (its `Advanced` pill measured at x=344).
       *
       * ⚠️ **THREE PILLS SHOULD FIT 320 AND THE WRAP IS NOT RELYING ON IT.** At
       * `max-sm:px-3` and `text-xs` the three come to roughly 176px against the
       * ~288px a full-width row has at 320 — comfortable, and **that is
       * arithmetic rather than a browser measurement**, which is precisely why
       * the class string that CAN wrap is the one used.
       *
       * ⛔ Do not "harmonise" this to the commissions string.
       */
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {PROPOSAL_VIEWS.map((spec) => (
        <Button
          key={spec.value}
          size="sm"
          variant={value === spec.value ? 'secondary' : 'ghost'}
          aria-pressed={value === spec.value}
          disabled={disabled}
          onClick={() => onChange(spec.value)}
        >
          {spec.label}
        </Button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Columns                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * ✅ **ONE ARRAY DRIVES THE HEADER, THE SKELETON AND THE TABLE'S FLOOR.** The
 * four-parallel-lists failure `/invoices` records costs a column shift as the
 * rows arrive, and a hand-carried `min-w-[…]` literal sat stale by 16px for
 * nine days — neither of which errors or is visible to `tsc`.
 */
interface ProposalColumn {
  key: string;
  label: string;
  width: number;
  /** The skeleton drawn in this column while the first page is in flight. */
  skeleton: string;
  /**
   * Sticks to the right edge while the table scrolls. ⚠️ `Table`'s `pinned` is
   * `sticky right-0`, so only the LAST column may carry it.
   */
  pinned?: boolean;
}

const PROPOSAL_COLUMNS: readonly ProposalColumn[] = [
  /* Who the enquiry is assigned to — FIRST, and code-pinned to the left edge. */
  {
    key: 'assignment',
    label: 'Assigned',
    width: ENQUIRY_ASSIGNMENT_COLUMN_WIDTH,
    skeleton: 'h-3 w-32',
  },
  /*
   * ⚠️ **THE HEADING SAYS *Institution*, NOT *Company*.** What this column
   * renders is free text the visitor typed; the company beneath it is the one a
   * person attached, and the two legitimately disagree. A heading reading
   * "Company" over a typed string claims a resolution nobody performed.
   */
  { key: 'institution', label: 'Institution', width: 240, skeleton: 'h-3 w-36' },
  { key: 'contact', label: 'Contact', width: 240, skeleton: 'h-3 w-32' },
  { key: 'services', label: 'Services', width: 220, skeleton: 'h-3 w-28' },
  { key: 'message', label: 'Message', width: 320, skeleton: 'h-3 w-48' },
  /*
   * ⚠️ **"Submitted" IS THE SENDER'S STAMP AND THE HEADING HAS TO SAY WHICH.**
   * A replayed enquiry carries Monday's `submittedAt` and Wednesday's
   * `createdAt`, and the two differ by days. This column orders on when the
   * institution got in touch, which is the question a reader of a lead log has.
   */
  { key: 'submitted', label: 'Submitted', width: 170, skeleton: 'h-3 w-24' },
  { key: 'campaign', label: 'Campaign', width: 200, skeleton: 'h-3 w-24' },
  { key: 'reference', label: 'Reference', width: 180, skeleton: 'h-3 w-28' },
  /* One Actions menu, with the company and deal facts above it. */
  {
    key: 'actions',
    label: 'Actions',
    width: ENQUIRY_ACTIONS_COLUMN_WIDTH,
    skeleton: 'h-3 w-20',
    pinned: true,
  },
];

const ACTIONS_KEY = 'actions';
const COLUMN_KEYS: readonly string[] = PROPOSAL_COLUMNS.map((column) => column.key);
const COLUMN_BY_KEY = new Map(PROPOSAL_COLUMNS.map((column) => [column.key, column]));
/* Institution names the row; Actions is the only home of every row action. */
const ASSIGNMENT_KEY = 'assignment';
const LOCKED_KEYS = ['institution', ACTIONS_KEY] as const;
const PINNED_KEYS = [ACTIONS_KEY] as const;
/* Assigned is an ordinary column, managed through the column manager like any other. */
const COLUMN_LAYOUT_SCOPE = 'enquiries.proposals';

function lockReasonOf(key: string): string | undefined {
  if (key === 'institution') return 'The institution names each enquiry';
  if (key === ACTIONS_KEY) return 'Every row action lives only in this column';
  return undefined;
}

/** The text a service shows — the key where nobody has mapped it. */
function serviceLabel(service: ProposalService): string {
  return service.name ?? service.sourceKey;
}

function campaignLabel(tag: CampaignTag): string {
  return `${tag.kind}: ${tag.value}`;
}

/** Distinct labels across the loaded rows, as funnel options. */
function optionsOf(labels: readonly string[]) {
  return [...new Set(labels)]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map((label) => ({ value: label, label }));
}

/*
 * ⚠️ Every funnel but Assigned is PAGE-scoped: the list endpoint takes no other
 * per-column filter params, so these narrow the loaded page only. `sortKey` is present only where
 * `B2bProposalListParams['sort']` has the member.
 */
function proposalColumns(
  rows: readonly ProposalRow[],
  assigneeOptions: readonly ColumnFilterOption[] | null,
): ColumnSpec<ProposalRow>[] {
  return [
    /* SERVER-scoped — the one funnel here that narrows the whole list, as `assigneeId`. */
    { ...assignmentColumnSpec<ProposalRow>('Assigned', assigneeOptions, rows), sortKey: 'assignee' },
    {
      key: 'institution',
      label: 'Institution',
      get: (row) => row.institutionName,
      sortKey: 'institutionName',
    },
    {
      key: 'contact',
      label: 'Contact',
      get: (row) => row.contactName,
      sortKey: 'contactName',
    },
    {
      /* A SET per row, so `matches` answers and `get` serves emptiness only. */
      key: 'services',
      label: 'Services',
      get: (row) =>
        row.services.length === 0 ? null : row.services.map(serviceLabel).join(', '),
      options: optionsOf(rows.flatMap((row) => row.services.map(serviceLabel))),
      matches: (row, selected) =>
        row.services.some((service) => selected.includes(serviceLabel(service))),
      sortKey: 'services',
    },
    {
      key: 'message',
      label: 'Message',
      get: (row) => row.message,
      format: (value) => {
        const flat = value.replace(/\s+/g, ' ').trim();
        return flat.length > 60 ? `${flat.slice(0, 60)}…` : flat;
      },
      sortKey: 'message',
    },
    {
      key: 'submitted',
      label: 'Submitted',
      get: (row) => row.submittedAt,
      format: (value) => formatDateTime(value),
      sortKey: 'submittedAt',
    },
    {
      key: 'campaign',
      label: 'Campaign',
      get: (row) =>
        row.campaignTags.length === 0 ? null : row.campaignTags.map(campaignLabel).join(', '),
      options: optionsOf(rows.flatMap((row) => row.campaignTags.map(campaignLabel))),
      matches: (row, selected) =>
        row.campaignTags.some((tag) => selected.includes(campaignLabel(tag))),
      sortKey: 'campaign',
    },
    {
      key: 'reference',
      label: 'Reference',
      get: (row) => row.sourceReference,
      sortKey: 'reference',
    },
  ];
}

/** The header row, drawn the same way in both the skeleton and the real table. */
function HeaderCells({
  headRowRef,
  visibleKeys,
  headerFor,
  frozenSideOf,
  frozenOffsets,
}: {
  headRowRef: RefObject<HTMLTableRowElement | null>;
  visibleKeys: readonly string[];
  headerFor: TableFilters<ProposalRow>['headerFor'];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <TR ref={headRowRef}>
      {visibleKeys.map((key) => {
        const side = frozenSideOf(key);
        const column = COLUMN_BY_KEY.get(key);
        if (key === ACTIONS_KEY) {
          return (
            <TH
              key={key}
              pinned={side === undefined}
              frozen={side}
              frozenOffset={frozenOffsets[key]}
              style={{ width: column?.width }}
            >
              {column?.label ?? 'Actions'}
            </TH>
          );
        }
        return (
          <FilterableTH
            key={key}
            {...headerFor(key)}
            frozen={side}
            frozenOffset={frozenOffsets[key]}
          />
        );
      })}
    </TR>
  );
}

const SKELETON_ROWS = 5;
/* Owner's decision: 100 a page, the API's ceiling. */
const PER_PAGE = 100;

/* -------------------------------------------------------------------------- */
/* Cells                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The services ticked.
 *
 * ⛔ **AN UNRESOLVED SERVICE RENDERS THE KEY, IN THE SAME TONE AS A RESOLVED
 * ONE.** A `warning` tone would render the ordinary day-one state as a fault —
 * the owner ruled that an unmapped service never refuses a submission, so it is
 * not an error here either. The `title` says which state a reader is looking at,
 * because nothing else on the cell can.
 *
 * ⚠️ **`[]` IS LEGAL AND MUST NOT READ AS *did not ask***: a visitor who picked
 * only AI Visa Tutor sends an empty list, because that option has no slug. The
 * honest wording names no service rather than denying one.
 */
function ServicesCell({ services }: { services: readonly ProposalService[] }) {
  if (services.length === 0) {
    return (
      <span className="text-sm text-fg-muted" title="This enquiry names no service.">
        No service named
      </span>
    );
  }

  return (
    <span className="flex flex-wrap gap-1">
      {services.map((service) => (
        <Badge
          key={service.key}
          tone="neutral"
          variant="outline"
          {...(service.name === null
            ? { title: `No mapping names ${service.sourceKey} yet, so the sender's own key is shown.` }
            : {})}
        >
          {service.name ?? service.sourceKey}
        </Badge>
      ))}
    </span>
  );
}

/**
 * The campaign (UTM) tags, each as a neutral badge naming its kind in real text.
 *
 * ⛔ **NO TAGS RENDERS AN EM DASH AND NOTHING ELSE** — never "Direct" or
 * "Unknown", which would be a label nobody recorded. The badge is clamped by
 * layout, so the full value rides on its `title`.
 */
function CampaignCell({ tags }: { tags: readonly CampaignTag[] }) {
  if (tags.length === 0) {
    return <span className="text-sm text-fg-muted">—</span>;
  }

  return (
    <span className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge
          key={tag.kind}
          tone="neutral"
          variant="outline"
          className="max-w-full"
          title={`${tag.kind}: ${tag.value}`}
        >
          <span className="min-w-0 max-w-[10rem] truncate">
            {tag.kind}: {tag.value}
          </span>
        </Badge>
      ))}
    </span>
  );
}

/**
 * What the institution wrote.
 *
 * ⚠️ **CLAMPED BY LAYOUT, NEVER TRUNCATED BY STRING** — the field takes 16 000
 * characters and is newline-bearing, so one pasted email would otherwise set the
 * height of its whole row. The full text stays on the `title`.
 *
 * ⚠️ **`whitespace-pre-line`, because the newlines are the sender's.** And it is
 * rendered as TEXT: a string a stranger typed into a public form is never markup.
 *
 * Click to expand (owner 2026-10-26): a clamped message is a button that opens
 * the full text in place, and clicking again folds it. Only a message that the
 * clamp actually cuts is clickable, measured on the element (scrollHeight vs
 * clientHeight) and re-measured when the column width changes.
 */
function MessageCell({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const node = textRef.current;
    if (node === null || expanded) return;
    const measure = () => setClamped(node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [message, expanded]);

  const text = (
    <p
      ref={textRef}
      className={`m-0 whitespace-pre-line text-sm text-fg-secondary [overflow-wrap:anywhere] ${expanded ? '' : 'line-clamp-3'}`}
      title={expanded ? undefined : message}
    >
      {message}
    </p>
  );
  if (!clamped && !expanded) return text;
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={() => setExpanded((open) => !open)}
      className="block w-full cursor-pointer rounded-xs text-left"
    >
      {text}
      <span className="mt-1 block text-2xs font-medium text-fg-muted">{expanded ? 'Show less' : 'Show more'}</span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* The delete confirmation                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 🔴 **THE CONSEQUENCE NOBODY WOULD GUESS, AND THE WHOLE REASON BOTH ARCHIVE
 * AND DELETE EXIST.**
 *
 * `source_reference` is `NOT NULL UNIQUE` and **is the entire idempotency
 * story**: the sender replays after an outage, and a replay collides with that
 * row and is answered *already recorded*. **Deleting the row frees the
 * reference**, so a replay arriving afterwards is stored as a NEW enquiry —
 * with a `201`, and nothing failing. Archiving keeps the reference taken.
 *
 * ⛔ **NO SENTENCE HERE PROMISES AN UNDO, IN ANY FORM.** Not "you can archive it
 * instead later", not a softened "may". It says what happens and stops.
 *
 * ⚠️ **The dialog body is the documented exception to the no-helper-text rule
 * rather than a carve-out from it** — `ConfirmDialog` body text naming a
 * destructive consequence is one of the four kinds the rule keeps. ⛔ **The
 * BUTTON carries no sentence of its own and must not gain one.**
 */
function DeleteDialog({
  row,
  open,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  row: ProposalRow;
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      tone="danger"
      title="Delete this enquiry permanently?"
      confirmLabel="Delete permanently"
      loading={busy}
      {...(error !== null ? { error } : {})}
      description={
        <>
          <p className="m-0 [overflow-wrap:anywhere]">
            This deletes what {row.institutionName} sent on {formatDateTime(row.submittedAt)} —
            their message, their contact details and the consent they gave. It can't be
            recovered.
          </p>
          <p className="m-0 mt-2 [overflow-wrap:anywhere]">
            It also frees the reference <strong>{row.sourceReference}</strong>. That reference is
            what stops the sending system recording this enquiry twice, so a replay arriving
            afterwards is stored as a <strong>second enquiry</strong> rather than being refused.
            Archiving keeps the reference taken.
          </p>
        </>
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/* The panel                                                                  */
/* -------------------------------------------------------------------------- */

/** What a row action is asked to do. Resolves when the work has landed. */
export type ProposalRowAction = (row: ProposalRow) => Promise<void> | void;

/**
 * Which of the row's controls is mid-flight.
 *
 * ⚠️ **A spinner is a claim about ONE control**, so the busy state has to name
 * the action as well as the row — see the `busy` state for the defect that came
 * of naming only the row. ⛔ **Not a permission slug and not a route name**:
 * `archive` and `unarchive` are one route on the server and two controls here,
 * and this union describes the controls.
 */
type ProposalActionKind =
  | 'company'
  | 'contact'
  | 'assign'
  | 'convert'
  | 'archive'
  | 'unarchive'
  | 'delete';

export interface ProposalsPanelProps {
  /**
   * The id given to this panel's own heading, so a tab outside it can be
   * `aria-labelledby`'d at it — `Panel` spreads its props onto the `<section>`
   * for exactly this, and `PanelHeader` takes the `id`.
   *
   * ⚠️ **Optional deliberately.** The panel is mounted by a file this one does
   * not own, and a required prop that the mount does not happen to pass is a
   * build error in somebody else's file.
   */
  headingId?: string;
  /**
   * Raise a deal from this enquiry. **Any write role**, and withheld once the
   * row already links to one.
   *
   * ⚠️ **A CALLBACK RATHER THAN A REQUEST**, because the flow is built
   * elsewhere. ⚠️ *SUPERSEDED 2026-09-20: "— and because no route creates a deal
   * from a proposal today."* **`PATCH /submissions/:id/deal` exists**; the
   * callback seam survives its own justification, for the reason in the file
   * header.
   */
  onConvert?: ProposalRowAction;
  /**
   * Create a company for an enquiry that has none attached. Offered only on a
   * row with no company and only to a reader who may create companies.
   */
  onCreateCompany?: ProposalRowAction;
  /**
   * Create a contact from this enquiry (owner decision 2026-10-02). Same
   * contract as `onCreateCompany`: the row is held busy until it settles, then
   * re-read. Whether the item is offered is decided by `EnquiryRowActions`.
   */
  onCreateContact?: ProposalRowAction;
  /**
   * Assign or reassign this enquiry. Supplied only to a reader holding
   * `b2b-proposal.triage`; same contract as `onCreateCompany` — the row is held
   * busy until it settles, then re-read.
   */
  onAssign?: ProposalRowAction;
  /**
   * The Assigned funnel's options (the Settings list plus Unassigned), read
   * once by the page. `null` while unread or on a failed read.
   */
  assigneeOptions?: readonly ColumnFilterOption[] | null;
  /**
   * Archive this enquiry. **Anyone who may read it.**
   *
   * ⚠️ **Archive and unarchive are TWO controls over ONE route**
   * (`PATCH /submissions/:id/archive`), which is why they are two props: the
   * panel decides which to offer from `row.archived`, and a caller wiring one
   * body to both would have to re-derive that test.
   */
  onArchive?: ProposalRowAction;
  /** Put it back. **Anyone who may read it.** */
  onUnarchive?: ProposalRowAction;
  /**
   * Destroy it. **Administrators only, and only on an already-archived row** —
   * both gates are applied here so a caller cannot relax either.
   */
  onDelete?: ProposalRowAction;
  /**
   * Called after any row action SUCCEEDS — archive, unarchive, delete, and a
   * convert flow closing (which is when the deal link has landed) — so the page
   * can re-read its to-deal-with counts. ⚠️ A prop rather than this panel
   * calling `useEnquirySummary` itself: the page owns the ONE instance whose
   * counts the tab pills render, and a second instance here would refresh a
   * count nothing displays.
   */
  onChanged?: () => void;
}

export function ProposalsPanel({
  headingId,
  onConvert,
  onCreateCompany,
  onCreateContact,
  onAssign,
  assigneeOptions = null,
  onArchive,
  onUnarchive,
  onDelete,
  onChanged,
}: ProposalsPanelProps) {
  const { ability } = useAuth();
  const canCreateCompany = ability.canCreate('companies');
  const canCreateContact = ability.canCreate('contacts');

  /**
   * 🔴 **`null` IS *WE COULD NOT ASK*. `[]` IS *WE ASKED AND THERE ARE NONE*.**
   * Every count, every pager and every empty state below branches on which of
   * the two it is holding, and the distinction is the whole honesty of this
   * panel. ⛔ **Never `?? []` between the wire and the screen.**
   */
  const [rows, setRows] = useState<readonly ProposalRow[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  /**
   * Which population is being shown. **Opens on the working queue.**
   *
   * ⚠️ **The default is a real value here, not an absent key.** On the WIRE the
   * absent key means *everything*; on this screen the default is *live*, and the
   * two are deliberately not the same choice — see {@link archivedParamOf}.
   */
  const [view, setView] = useRememberedState<ProposalView>(
    'proposals-view',
    DEFAULT_VIEW,
    (raw) => PROPOSAL_VIEWS.find((spec) => spec.value === raw)?.value,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * ⚠️ **A `403` IS A REFUSAL AND NOT A FAILURE, AND IT IS HELD SEPARATELY SO
   * THE TWO CANNOT SHARE A SENTENCE.** `/credit-notes` shipped *"Couldn't load
   * credit notes."* over the server's *"Requires the … permission"* — a failure
   * framing on a refusal, which sends a reader to retry and then to report a
   * broken page. **A retry is refused identically for as long as the grant is
   * absent.** The split is on the STATUS, never on the message: matching the
   * prose would be this codebase's own *never match on a message* rule arriving
   * on a refusal.
   */
  const [denied, setDenied] = useState<string | null>(null);

  const [confirmingDelete, setConfirmingDelete] = useState<ProposalRow | null>(null);
  /**
   * 🔴 **WHICH ROW *AND* WHICH ACTION, NOT JUST WHICH ROW.**
   *
   * ⚠️ *SUPERSEDED 2026-09-20: `const [busyId, setBusyId] = useState<number | null>(null)`.*
   * Every control on the row read `loading={busy}` off that one id, so pressing
   * **Archive** put a spinner on **Convert to a deal** as well — a control
   * reporting an in-flight conversion nobody started, on the row where a
   * conversion is the consequential act. Nothing errored and no request was
   * made; the screen simply said something that was not happening.
   *
   * ⚠️ **The ROW half still drives `anyBusy`**, because a second write anywhere
   * would race the re-read that follows the first.
   */
  const [busy, setBusy] = useState<{ id: number; kind: ProposalActionKind } | null>(null);
  /** Beside the button that was pressed — never a panel-level banner a reader scrolling a table would never see. */
  const [actionFailure, setActionFailure] = useState<{ id: number; message: string } | null>(null);

  /**
   * ⚠️ **A COUNTER RATHER THAN A DERIVED CONDITION IN THE DEPENDENCY ARRAY.**
   * Re-reading because `rows` went back to `null` reads neatly and couples the
   * refetch to a value the FAILURE path also writes — so a failed read would
   * re-enter the effect and retry for ever, quietly, against a server that has
   * just refused. A counter fires exactly when somebody asks it to.
   */
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((current) => current + 1), []);

  /* The column manager — see ContactsPage, which this follows. */
  const layout = useColumnLayout(
    COLUMN_LAYOUT_SCOPE,
    COLUMN_KEYS,
    LOCKED_KEYS,
    PINNED_KEYS,
  );
  const visibleKeys = layout.visibleKeys;
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, layout.frozenSideOf, PINNED_KEYS);
  /* Summed from the VISIBLE columns, so a hidden one takes its width with it. */
  const tableMinWidth = useMemo(
    () => visibleKeys.reduce((sum, key) => sum + (COLUMN_BY_KEY.get(key)?.width ?? 0), 0),
    [visibleKeys],
  );
  const labelOf = useCallback(
    (key: string): string => COLUMN_BY_KEY.get(key)?.label ?? key,
    [],
  );

  const loadedRows = useMemo(() => (rows === null ? [] : [...rows]), [rows]);
  const columns = useMemo(
    () => proposalColumns(loadedRows, assigneeOptions),
    [loadedRows, assigneeOptions],
  );
  const table = useTableFilters(loadedRows, columns);
  const { visibleRows, sort, serverParams } = table;
  /* The Assigned funnel's one server selection, or `null` to omit the key. */
  const assigneeId = assigneeFilterOf(serverParams[ASSIGNEE_PARAM]);

  /*
   * A new ordering or a new server-side narrowing starts from page 1; adjusted
   * during render, not in an effect. Page 3 of a shorter result reads as
   * "nothing matches" when every match is on page 1.
   */
  const sortSignature = `${sort === null ? '' : `${sort.key}:${sort.direction}`}|${assigneeId ?? ''}`;
  const [sortSeen, setSortSeen] = useState(sortSignature);
  if (sortSeen !== sortSignature) {
    setSortSeen(sortSignature);
    setPage(1);
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    /*
     * 🔴 **THE VIEW'S KEY, AND `all` OMITS IT ENTIRELY.** Spread rather than
     * passed as `archived: undefined`, so *everything* is expressed the only
     * way the wire can express it — see {@link archivedParamOf}.
     */
    const params: B2bProposalListParams = { page, perPage: PER_PAGE, ...archivedParamOf(view) };
    /* Sent only once a header has been used; the server has its own default. */
    if (sort !== null) {
      params.sort = sort.key as B2bProposalListParams['sort'];
      params.order = sort.direction;
    }
    if (assigneeId !== null) params.assigneeId = assigneeId;

    api
      .listB2bProposalSubmissions(params, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        const pages = Math.max(answer.meta.totalPages, 1);

        /*
         * 🔴 **THE STRANDED PAGE.** Archive or delete the last row of the last
         * page and `totalPages` drops while `page` stays where it was — so the
         * pager UNMOUNTS (`totalPages > 1` is false) and the reader is left on
         * a page past the end, reading an empty state that claims something
         * about the business, **with no control on screen to leave it.**
         *
         * ⛔ **THE CLAIM IS THE DEFECT, NOT THE EMPTINESS.** "No enquiries yet"
         * over page 3 of a 2-page list is a statement about the log made from a
         * page that is merely out of range.
         *
         * ⚠️ **IT RETURNS WITHOUT PAINTING, AND `loading` IS LEFT TRUE**, so the
         * past-the-end answer never reaches the screen for a frame. `pages` is
         * strictly less than `page` in this branch, so `setPage` always changes
         * the value and the effect always re-enters — there is no state in which
         * this leaves the panel loading for ever.
         */
        if (page > pages) {
          setTotalPages(pages);
          setPage(pages);
          return;
        }

        setRows(answer.data.map(rowOf));
        setTotal(answer.meta.total);
        setTotalPages(pages);
        setError(null);
        setDenied(null);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        /*
         * ⛔ **THE ROWS AND THE COUNT GO BACK TO `null` TOGETHER.** Leaving a
         * stale count beside a failure banner is the defect this file's header
         * records: a figure that describes a read which did not happen.
         */
        setRows(null);
        setTotal(null);
        setLoading(false);
        if (caught instanceof ApiError && caught.status === 403) {
          setDenied(caught.message);
          setError(null);
          return;
        }
        setDenied(null);
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'The enquiries couldn’t be loaded. Check your connection and try again.',
        );
      });

    /*
     * ⚠️ **`setLoading(false)` MOVED OUT OF A `.finally` AND INTO BOTH ARMS**,
     * because the clamp above needs one path that deliberately stays loading.
     * A `.finally` runs after every settle including that one, which would
     * paint the out-of-range page for exactly as long as the re-read takes.
     */
    return () => controller.abort();
  }, [page, view, reloadKey, sort, assigneeId]);

  /**
   * Runs a row action and reports its outcome beside the control.
   *
   * ⚠️ **NO `AbortSignal` IS PASSED DOWN.** Aborting a write cancels the
   * LISTENING and not the write, so offering to cancel would report an outcome
   * nobody can know — the ruling `DeleteSubmissionControl` already records.
   */
  const run = useCallback(
    (row: ProposalRow, kind: ProposalActionKind, action: ProposalRowAction, after?: () => void) => {
      setBusy({ id: row.id, kind });
      setActionFailure(null);
      Promise.resolve()
        .then(() => action(row))
        .then(() => {
          setBusy(null);
          after?.();
          /* The list is stale whatever the action was: a row has moved, gone,
             or gained a deal. Re-read rather than patching a local copy. */
          reload();
          onChanged?.();
        })
        .catch((caught: unknown) => {
          setBusy(null);
          setActionFailure({
            id: row.id,
            message:
              caught instanceof ApiError
                ? caught.message
                : 'That didn’t go through. Try again.',
          });
        });
    },
    [reload, onChanged],
  );

  /**
   * 🔴 **THE PAGE GOES BACK TO 1 ON EVERY VIEW CHANGE.**
   *
   * The house rule is *a filter change resets to page 1*, and `/users` shipped
   * the defect it exists to prevent: filtering from page 3 asked the server for
   * page 3 of a much shorter result, got an empty page, and **the emptiness read
   * as "nothing matches" when every match was sitting on page 1.**
   *
   * ⚠️ **The three populations have three different lengths by construction**,
   * so this is not a precaution — a live queue of 2 pages and an archive of 40
   * make the stale page wrong in one direction or the other almost every time.
   *
   * ⚠️ **The early return is not an optimisation.** Re-selecting the pill you
   * are already on would otherwise reset the page, which is a control silently
   * undoing a reader's paging on a press that changed nothing.
   *
   * ⛔ **THE THREE SETTERS ARE CALLED SIDE BY SIDE, NEVER FROM INSIDE A
   * `setView` UPDATER.** An updater must be pure: React invokes it twice in
   * StrictMode, so a `setPage` nested in one runs twice — harmless here by luck,
   * and the pattern is how a genuinely non-idempotent effect gets doubled. The
   * three are batched into one render either way.
   */
  const changeView = useCallback(
    (next: ProposalView) => {
      if (next === view) return;
      setView(next);
      setPage(1);
      setActionFailure(null);
    },
    [view, setView],
  );

  const spec = viewSpecOf(view);
  const firstRow = total === null || total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === null ? 0 : Math.min(page * PER_PAGE, total);

  /**
   * ⚠️ **A DELETE FAILURE IS REPORTED ONCE, IN THE DIALOG THAT IS OVER IT.**
   * `ConfirmDialog` renders its own `error`, and the same message was also
   * rendering beside the row's buttons behind it — one refusal, twice, the
   * second copy visible the moment the dialog closes and reading as a second
   * failure. The dialog outranks because it is what the reader is looking at.
   */
  const failureFor = (row: ProposalRow): string | null =>
    actionFailure?.id === row.id && confirmingDelete?.id !== row.id ? actionFailure.message : null;

  /** Which of this row's controls is spinning, if any. ⚠️ Row AND action. */
  const busyKindFor = (row: ProposalRow): ProposalActionKind | null =>
    busy?.id === row.id ? busy.kind : null;

  /*
   * The pager renders only over an array, for the same reason the count does:
   * "Page 1 of 1" over a read that never came back is a claim. Shared by the
   * table and the filtered-empty state, so a page the filters emptied can still
   * be left.
   */
  const pager =
    totalPages > 1 ? (
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
        <span className="text-xs text-fg-muted tabular">
          {total === null ? `Page ${page}` : `${firstRow}–${lastRow} of ${total}`}
        </span>
        <span className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => Math.max(current - 1, 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
          >
            Next
          </Button>
        </span>
      </div>
    ) : null;

  return (
    <Panel {...(headingId !== undefined ? { 'aria-labelledby': headingId } : {})}>
      <PanelHeader
        {...(headingId !== undefined ? { id: headingId } : {})}
        title="Proposal enquiries"
        /*
         * 🔴 **THE COUNT RENDERS ONLY OVER AN ARRAY.** A failed read prints
         * nothing here — not a `0`, not a dash, not a stale figure — and it is
         * in no live region, so nothing is spoken either.
         *
         * ⚠️ **`total` rather than `rows.length`**: the list is paged, so the
         * page's length answers a different question from the one the heading
         * appears to ask. ⚠️ **`total === null` with rows in hand means the
         * server sent no total**, which is *not known* and is rendered as
         * nothing rather than as the page's length.
         *
         * 🔴 **AND THE NOUN NAMES THE POPULATION THE FIGURE DESCRIBES.** The
         * list is filtered, so a bare *"20 enquiries"* over the archive is a
         * confident claim about the wrong set — twenty exist, says the heading,
         * when twenty are merely filed away and the log may hold four hundred.
         * The nouns live on {@link PROPOSAL_VIEWS} beside the pill labels, so
         * the control and the count cannot come to describe different things.
         *
         * ⛔ **THE TOGGLE IS NOT IN THIS SLOT.** `PanelHeader`'s `actions` is
         * `flex shrink-0`, and this repo's standing rule is that a bank of
         * filter controls does not belong in it — it cannot wrap, which is the
         * clipped-pill failure the toggle's own comment records. It renders in a
         * full-width row of its own below.
         */
        actions={
          rows === null ? (
            loading ? (
              <Skeleton className="h-3 w-24" />
            ) : null
          ) : total === null ? null : (
            <span className="text-xs text-fg-muted tabular">
              {pluralise(total, spec.singular, spec.plural)}
            </span>
          )
        }
      />

      {/*
        ⚠️ **THE TOGGLE IS WITHHELD ONLY ON A REFUSAL**, and renders over a
        failed read on purpose: switching view re-reads, so it is the one
        recovery on screen. ⛔ **A refused reader gets no filter**, because a
        control whose only outcome is another refusal is the
        affordance-with-one-outcome this repo refuses everywhere.

        ⚠️ **Disabled while a read is in flight, never hidden.** A pill that
        disappears is a feature reported as deleted; a pill that is briefly
        inert is a control doing what it says.
      */}
      {denied === null && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <ViewToggle value={view} onChange={changeView} disabled={loading} />
          {/* Hidden below `md`, where there is no table to arrange. */}
          <div className="hidden md:block">
            <ColumnManager
              layout={layout}
              labelOf={labelOf}
              lockedKeys={LOCKED_KEYS}
              lockReasonOf={lockReasonOf}
              pinnedKeys={PINNED_KEYS}
            />
          </div>
        </div>
      )}

      {denied === null && rows !== null && table.activeCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border bg-surface-sunken px-4 py-2">
          <p className="m-0 text-xs text-fg-secondary">
            {pluralise(table.activeCount, 'column filter')} applied.{' '}
            {rows.length - visibleRows.length > 0
              ? `${rows.length - visibleRows.length} of the ${pluralise(rows.length, 'row')} loaded here are hidden.`
              : 'Every loaded row still matches.'}
          </p>
          <Button variant="secondary" size="sm" onClick={table.clearAll}>
            Clear column filters
          </Button>
        </div>
      )}

      {/*
        ⚠️ **A `danger`-TONED BLOCK NAMING WHAT FAILED**, above the content rather
        than instead of it, carrying the server's own sentence rather than a
        house paraphrase. `role="alert"` so it is announced when it arrives.
      */}
      {error !== null && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load enquiries.</>}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        </div>
      )}

      {denied !== null ? (
        /*
         * ⛔ **THE REFUSAL REPLACES THE CONTENT RATHER THAN SITTING ABOVE AN
         * EMPTY TABLE.** An empty table reads as *nobody has enquired*, which is
         * a statement about the business rather than about this reader's access.
         *
         * ⚠️ **The server's sentence beside ours**, because it names the grant;
         * ours names what the reader cannot do. Neither is worded as a failure.
         */
        <EmptyState
          icon={<EventInterestIcon />}
          title="Proposal enquiries are restricted"
          /* Ours names what the reader cannot do; the server's names the grant.
             Joined only when there is one, so a server that says nothing does
             not leave a trailing space in a sentence a person reads. */
          description={
            denied === ''
              ? 'Your account is not allowed to read proposal enquiries.'
              : `Your account is not allowed to read proposal enquiries. ${denied}`
          }
        />
      ) : loading && rows === null ? (
        <>
          {/* Below `md`: skeleton cards, in the shape of the real ones. */}
          <div className="md:hidden">
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <div key={index} className="border-b border-border px-4 py-3 last:border-b-0">
                <Skeleton className="h-3 w-36" />
                <Skeleton className="mt-2 h-2.5 w-28" />
                <Skeleton className="mt-2 h-2.5 w-full" />
              </div>
            ))}
          </div>
          <div className="hidden md:block">
            <TableWrap frozenHeader>
              {/*
                ⚠️ **THE FLOOR GOES THROUGH `style`, NOT A CLASS.** Tailwind v4
                scans SOURCE TEXT, so an interpolated `min-w-[…]` emits no rule —
                the class lands in the DOM and does nothing.
              */}
              <Table style={{ minWidth: tableMinWidth }}>
                <THead>
                  <HeaderCells
                    headRowRef={headRowRef}
                    visibleKeys={visibleKeys}
                    headerFor={table.headerFor}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </THead>
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <TR key={index}>
                      {visibleKeys.map((key) => {
                        const side = layout.frozenSideOf(key);
                        return (
                          <TD
                            key={key}
                            pinned={key === ACTIONS_KEY && side === undefined}
                            frozen={side}
                            frozenOffset={frozenOffsets[key]}
                          >
                            <Skeleton className={COLUMN_BY_KEY.get(key)?.skeleton ?? 'h-3 w-20'} />
                          </TD>
                        );
                      })}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>
        </>
      ) : rows === null ? (
        /*
         * A read that did not come back. ⛔ **No count, no pager, and copy that
         * points at the banner above rather than restating the server's
         * message** — one copy of a refusal, in the place that has all of it.
         */
        <EmptyState
          icon={<EventInterestIcon />}
          title="Nothing to show"
          description="The enquiries couldn’t be loaded. Check the message above and try again."
        />
      ) : rows.length === 0 ? (
        /*
         * ⛔ **NO COPY HERE IMPLIES DATA IS ON ITS WAY.** An empty log is an
         * ordinary, permanent state — and an empty state that hedges about it is
         * how a dead endpoint is mistaken for a quiet week.
         *
         * 🔴 **AND IT NAMES THE VIEW, BECAUSE THE LIST IS NARROWED.** *"No
         * enquiries yet"* over the **Archived** view says nobody has ever
         * enquired, when what is true is that nobody has archived one — a
         * statement about the business standing in for a statement about a
         * filter. Each view's wording is on {@link PROPOSAL_VIEWS}, beside the
         * noun its count uses, so the two cannot drift apart.
         */
        <EmptyState
          icon={<EventInterestIcon />}
          title={spec.emptyTitle}
          description={spec.emptyDescription}
        />
      ) : visibleRows.length === 0 ? (
        /* Rows were loaded and the column filters hid every one — not an empty log. */
        <>
          <EmptyState
            icon={<EventInterestIcon />}
            title="No enquiries match the column filters"
            description={`${pluralise(rows.length, 'row')} loaded on this page; the filters hide all of them.`}
            action={
              <Button variant="secondary" size="sm" onClick={table.clearAll}>
                Clear column filters
              </Button>
            }
          />
          {pager}
        </>
      ) : (
        <>
          {/* ── Below `md`: a card per enquiry ──────────────────────────────
              The table's columns will not fit 320px, and every fact is on the card:
              a value that exists only above a breakpoint is indistinguishable
              from one that was removed, and this app has had a working control
              reported as deleted for exactly that. */}
          <div className="md:hidden">
            {visibleRows.map((row) => (
              <article key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                {/* The Assigned column leads the table, so it leads the card. */}
                <div className="mb-2 min-w-0">
                  <EnquiryAssignmentCell assignment={row.assignment} />
                </div>
                <InstitutionCell row={row} />
                <div className="mt-2">
                  <ContactCell row={row} />
                </div>
                <div className="mt-2">
                  <ServicesCell services={row.services} />
                </div>
                <div className="mt-2">
                  <MessageCell message={row.message} />
                </div>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <dt className="m-0 text-fg-muted">Submitted</dt>
                  <dd className="m-0 text-fg-secondary">{formatDateTime(row.submittedAt)}</dd>
                  <dt className="m-0 text-fg-muted">Campaign</dt>
                  <dd className="m-0 min-w-0">
                    <CampaignCell tags={row.campaignTags} />
                  </dd>
                  <dt className="m-0 text-fg-muted">Reference</dt>
                  <dd className="m-0 text-fg-secondary [overflow-wrap:anywhere]">
                    {row.sourceReference}
                  </dd>
                </dl>
                <div className="mt-2">
                  <RowActions
                    row={row}
                    mayDelete={ability.canDelete('b2b-proposal')}
                    canConvert={ability.canCreate('deals')}
                    canCreateCompany={canCreateCompany}
                    canCreateContact={canCreateContact}
                    busyKind={busyKindFor(row)}
                    anyBusy={busy !== null}
                    failure={failureFor(row)}
                    {...(onCreateCompany
                      ? { onCreateCompany: () => run(row, 'company', onCreateCompany) }
                      : {})}
                    {...(onCreateContact
                      ? { onCreateContact: () => run(row, 'contact', onCreateContact) }
                      : {})}
                    {...(onAssign ? { onAssign: () => run(row, 'assign', onAssign) } : {})}
                    {...(onConvert ? { onConvert: () => run(row, 'convert', onConvert) } : {})}
                    {...(onArchive ? { onArchive: () => run(row, 'archive', onArchive) } : {})}
                    {...(onUnarchive
                      ? { onUnarchive: () => run(row, 'unarchive', onUnarchive) }
                      : {})}
                    {...(onDelete ? { onDelete: () => setConfirmingDelete(row) } : {})}
                  />
                </div>
              </article>
            ))}
          </div>

          {/* ── From `md`: the table ──────────────────────────────────────── */}
          <div className="hidden md:block">
            <TableWrap frozenHeader>
              <Table style={{ minWidth: tableMinWidth }}>
                <THead>
                  <HeaderCells
                    headRowRef={headRowRef}
                    visibleKeys={visibleKeys}
                    headerFor={table.headerFor}
                    frozenSideOf={layout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </THead>
                <TBody>
                  {visibleRows.map((row) => {
                    const cells: Record<string, ReactNode> = {
                      [ASSIGNMENT_KEY]: (
                        <div className="min-w-0">
                          <EnquiryAssignmentCell assignment={row.assignment} />
                        </div>
                      ),
                      institution: <InstitutionCell row={row} />,
                      contact: <ContactCell row={row} />,
                      services: <ServicesCell services={row.services} />,
                      message: <MessageCell message={row.message} />,
                      submitted: (
                        <span className="text-sm text-fg-secondary">
                          {formatDateTime(row.submittedAt)}
                        </span>
                      ),
                      campaign: <CampaignCell tags={row.campaignTags} />,
                      reference: (
                        <span className="text-sm text-fg-secondary [overflow-wrap:anywhere]">
                          {row.sourceReference}
                        </span>
                      ),
                      [ACTIONS_KEY]: (
                        <RowActions
                          row={row}
                          mayDelete={ability.canDelete('b2b-proposal')}
                          canConvert={ability.canCreate('deals')}
                          canCreateCompany={canCreateCompany}
                          canCreateContact={canCreateContact}
                          busyKind={busyKindFor(row)}
                          anyBusy={busy !== null}
                          failure={failureFor(row)}
                          {...(onCreateCompany
                            ? { onCreateCompany: () => run(row, 'company', onCreateCompany) }
                            : {})}
                          {...(onCreateContact
                            ? { onCreateContact: () => run(row, 'contact', onCreateContact) }
                            : {})}
                          {...(onAssign
                            ? { onAssign: () => run(row, 'assign', onAssign) }
                            : {})}
                          {...(onConvert
                            ? { onConvert: () => run(row, 'convert', onConvert) }
                            : {})}
                          {...(onArchive ? { onArchive: () => run(row, 'archive', onArchive) } : {})}
                          {...(onUnarchive
                            ? { onUnarchive: () => run(row, 'unarchive', onUnarchive) }
                            : {})}
                          {...(onDelete ? { onDelete: () => setConfirmingDelete(row) } : {})}
                        />
                      ),
                    };
                    return (
                      <TR key={row.id}>
                        {visibleKeys.map((key) => {
                          const side = layout.frozenSideOf(key);
                          return (
                            <TD
                              key={key}
                              pinned={key === ACTIONS_KEY && side === undefined}
                              frozen={side}
                              frozenOffset={frozenOffsets[key]}
                            >
                              {cells[key]}
                            </TD>
                          );
                        })}
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          {pager}
        </>
      )}

      {confirmingDelete !== null && onDelete && (
        <DeleteDialog
          row={confirmingDelete}
          open
          /* ⚠️ The DELETE specifically — a row mid-archive must not put a
             spinner on a confirmation for a different action. */
          busy={busy?.id === confirmingDelete.id && busy.kind === 'delete'}
          error={actionFailure?.id === confirmingDelete.id ? actionFailure.message : null}
          onClose={() => {
            setConfirmingDelete(null);
            setActionFailure(null);
          }}
          onConfirm={() => {
            const row = confirmingDelete;
            run(row, 'delete', onDelete, () => setConfirmingDelete(null));
          }}
        />
      )}
    </Panel>
  );
}

/**
 * The institution as it was typed, with the attached company beneath it.
 *
 * ⚠️ **BOTH, NEVER ONE COALESCED OVER THE OTHER.** One is what somebody wrote in
 * a box; the other is what a person decided it meant, and they legitimately
 * disagree. ⛔ **No sentence calls an unattached enquiry incomplete** — every
 * enquiry arrives unlinked and most of them stay that way.
 *
 * 🔴 **AND IT CARRIES THE `Archived` MARKER, WHICH IS A REAL WORD.** In the
 * **All** view the two populations are interleaved, and before this the only
 * tell that a row was archived was that its button read `Unarchive` — a fact
 * about a CONTROL standing in for a fact about the RECORD, invisible to anyone
 * whose grant withholds the control and invisible on a row where the callback
 * is not supplied. ⛔ **Tone is never the only signal**; the badge says the word.
 *
 * ⚠️ **IT RENDERS ON EVERY ARCHIVED ROW, INCLUDING IN THE ARCHIVED VIEW WHERE
 * EVERY ROW HAS IT.** Redundant there, and deliberately so: a marker whose
 * presence depends on which pill is pressed is a second rule to hold, and its
 * absence would then mean *live* in one view and *you are already filtered* in
 * another. **One meaning, always.**
 *
 * ⚠️ **It sits with the institution rather than in the actions lane**, because a
 * reader scanning the All view is asking *which of these are put away*, which is
 * a question about identity, and because it then renders in both the table and
 * the phone card from one place.
 */
function InstitutionCell({ row }: { row: ProposalRow }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium text-fg [overflow-wrap:anywhere]">
          {row.institutionName}
        </span>
        {row.archived && (
          <Badge tone="neutral" title="Somebody took this off the Live view.">
            Archived
          </Badge>
        )}
        {row.noConsentRecorded && (
          <Badge
            tone="neutral"
            title="Imported from before the website recorded consent."
          >
            No consent recorded
          </Badge>
        )}
      </span>
      {row.companyName !== null && (
        <span className="text-xs text-fg-muted [overflow-wrap:anywhere]">{row.companyName}</span>
      )}
      {/* Where the institution is. Rendered in both the table and the phone
          card from here, so the two cannot disagree; an unrecorded location is
          the house em dash, never a word nobody recorded. */}
      <span className="text-xs text-fg-subtle [overflow-wrap:anywhere]">
        <span className="text-fg-muted">Country</span> {orDash(row.location)}
      </span>
    </span>
  );
}

/**
 * Who got in touch.
 *
 * ⚠️ **The email is rendered as it was SENT, not lower-cased** — the column
 * stores what the visitor typed, and normalising it here would print something
 * the sender never wrote. ⚠️ **And the phone is rendered as it arrived**: it can
 * legitimately carry no dial code, and prepending one guesses a country.
 */
function ContactCell({ row }: { row: ProposalRow }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-sm text-fg [overflow-wrap:anywhere]">{row.contactName}</span>
      <EmailLink address={row.contactEmail} tone="muted" className="text-xs" />
      {row.contactDesignation !== null && (
        <span className="text-xs text-fg-subtle [overflow-wrap:anywhere]">
          {row.contactDesignation}
        </span>
      )}
      {row.contactPhone !== null && (
        <span className="text-xs text-fg-subtle [overflow-wrap:anywhere]">{row.contactPhone}</span>
      )}
    </span>
  );
}

/**
 * The row's controls.
 *
 * 🔴 **EVERY GATE IS HERE RATHER THAN AT THE TWO CALL SITES**, so the table and
 * the card cannot disagree about who may destroy a record — the ruling
 * `InlineTrackerFields` records for the same shape: **suppress where the
 * component decides, not where it is used.** This component is mounted twice on
 * every render of the panel, once per breakpoint.
 *
 * ⛔ **WITHHELD, NOT DISABLED, IN EVERY CASE.** A disabled `Button` carries
 * `disabled:pointer-events-none`, so it never receives a hover and a `title` on
 * it never renders — a why-this-is-disabled sentence would need a wrapper span,
 * which is a sentence beside a control on a dense row that has no room for one.
 * The one thing rendered in a control's place is a **value** (the deal badge),
 * which explains itself.
 *
 * ⚠️ **AN ACTION WITH NO HANDLER IS WITHHELD TOO.** The behaviour is supplied by
 * whoever mounts this panel, and an affordance whose only outcome is nothing is
 * worse than no affordance.
 */
function RowActions({
  row,
  mayDelete,
  canConvert,
  canCreateCompany,
  canCreateContact,
  busyKind,
  anyBusy,
  failure,
  onCreateCompany,
  onCreateContact,
  onAssign,
  onConvert,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  row: ProposalRow;
  /**
   * 🔴 **ADMINISTRATORS ONLY FOR THE DELETE, AND DELIBERATELY NOT
   * `canDelete('b2b-proposal')` — BUT NOT FOR THE REASON THIS COMMENT USED TO
   * GIVE.**
   *
   * ⚠️ *SUPERSEDED 2026-09-20: "there is no `b2b-proposal` member of
   * `ModuleSlug`, and an invented slug resolves to nothing — which **degrades
   * OPEN**, on a destructive control."* **That is false.** The member is
   * declared at `src/lib/permissions.ts:521` and the slug is in
   * `OPEN_WRITE_MODULES` at `:736`, so `canDelete('b2b-proposal')` resolves to a
   * real grant and would compile, run and answer.
   *
   * 🔴 **THE LIVE REASON IS AN OWNER RULING, AND IT IS WHY THE CALL WOULD BE
   * WRONG RATHER THAN MERELY UNAVAILABLE.** `permissions.ts:513` states it at
   * the slug: **the three tiers are deliberately non-uniform — archive on the
   * READ grant, delete ADMIN, convert any write role — and swapping either test
   * for `canDelete('b2b-proposal')` "would hand the delete to whatever the grant
   * says and quietly overturn that ruling."**
   *
   * ⛔ **A DEAD REASON ON A LIVE GATE IS THE DANGEROUS HALF.** A reader who
   * checks the old premise finds it false and concludes the gate should become
   * `canDelete(...)` — which is precisely the edit the ruling forbids, arriving
   * as a correction. Right answer, wrong reason, nothing failing.
   *
   * ⚠️ **This is the optimisation and not the guarantee.** A role can be taken
   * away under a live access token, which lasts about five days, so the handler
   * still has to survive a refusal.
   *
   * 🔴 *SUPERSEDED 2026-10-01, the tier ruling above:* deletes follow the set,
   * so this is `canDelete('b2b-proposal')` (owner's decision).
   */
  mayDelete: boolean;
  /**
   * **Any write role**, asked as the question the server will be asked: a
   * convert raises a DEAL, so `canCreate('deals')` is the grant that decides it.
   *
   * ⚠️ **NOT `canCreate('b2b-proposal')`** — same ruling as the delete above.
   * The convert writes a deal, so the deal's module is what decides it.
   */
  canConvert: boolean;
  /** `canCreate('companies')` — the grant the create itself is asked under. */
  canCreateCompany: boolean;
  /** `canCreate('contacts')` — the grant the contact create is asked under. */
  canCreateContact: boolean;
  /**
   * 🔴 **WHICH OF THIS ROW'S CONTROLS IS SPINNING, OR `null`.**
   *
   * ⚠️ *SUPERSEDED 2026-09-20: `busy: boolean`.* Every control read the same
   * flag, so pressing **Archive** span the **Convert to a deal** button beside
   * it — **a control reporting an in-flight conversion nobody started.** A
   * spinner is a claim about one control, so the state has to name one.
   */
  busyKind: ProposalActionKind | null;
  /** Another row is mid-action; a second write would race the re-read. */
  anyBusy: boolean;
  failure: string | null;
  onCreateCompany?: () => void;
  onCreateContact?: () => void;
  /** Supplied only to a triager — the page passes `onAssign` inside its triage block. */
  onAssign?: () => void;
  onConvert?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
}) {
  /* 🔴 THE GATE IS THE ROW'S, NOT THIS COMPONENT'S — see ProposalRow.convertible.
     ⚠️ SUPERSEDED: `const convertible = row.dealId === null;`, a second copy of
     a rule `proposalIsConvertible` already owned, and it disagreed with it: a
     converted enquiry whose deal was later deleted is `dealId === null`, so the
     button appeared and the flow then refused it.
     A BADGE rather than a disabled button: it is a fact about the row, not a
     refused control. */
  const convertible = row.convertible;
  const { ability } = useAuth();

  /*
   * Owner's decision 2026-10-01: one Actions menu, the status links above it.
   * Every gate below is unchanged from the stacked buttons it replaced; a
   * withheld action is simply absent from the menu.
   *
   * ⚠️ **`busy` NAMES THE ONE ACTION; `disabled` STAYS ROW-WIDE.** The spinner
   * is a claim about THIS action, while the disable is a race guard and is
   * right to fire on any write anywhere.
   */
  const items: EnquiryActionItem[] = [];
  if (onAssign) {
    items.push({
      key: 'assign',
      label: row.assignment ? 'Reassign…' : 'Assign to…',
      onSelect: onAssign,
      busy: busyKind === 'assign',
    });
  }
  if (row.companyId === null && canCreateCompany && onCreateCompany) {
    items.push({
      key: 'company',
      label: 'Create a company',
      onSelect: onCreateCompany,
      busy: busyKind === 'company',
    });
  }
  if (convertible && canConvert && onConvert) {
    items.push({
      key: 'convert',
      label: 'Convert to a deal',
      onSelect: onConvert,
      busy: busyKind === 'convert',
    });
  }
  if (!row.archived && onArchive) {
    items.push({ key: 'archive', label: 'Archive', onSelect: onArchive, busy: busyKind === 'archive' });
  }
  if (row.archived && onUnarchive) {
    items.push({
      key: 'unarchive',
      label: 'Unarchive',
      onSelect: onUnarchive,
      busy: busyKind === 'unarchive',
    });
  }
  /*
   * 🔴 **BOTH GATES, AND THE SECOND IS THE ONE A LATER PASS WILL DROP.** An
   * administrator may delete, and only a row that has already been archived.
   * Archiving first is the step that makes a permanent delete a decision rather
   * than a slip — see {@link DeleteDialog} for what is actually lost.
   */
  if (row.archived && mayDelete && onDelete) {
    items.push({ key: 'delete', label: 'Delete permanently', onSelect: onDelete, danger: true });
  }

  return (
    <EnquiryRowActions
      rowName={row.institutionName}
      company={row.companyId !== null ? { id: row.companyId, name: row.companyName } : null}
      /* ⚠️ TWO DIFFERENT FACTS, and "Deal raised" is false about the second. A
         deal that has since been deleted must not be reported as existing — and
         saying so is the only thing on screen explaining why no Convert item is
         offered on a row with no deal against it. */
      deal={
        convertible
          ? null
          : row.dealId !== null
            ? { kind: 'raised', id: row.dealId }
            : { kind: 'deleted' }
      }
      /* EnquiryRowActions decides the "Create a contact" item from this; it is
         deliberately not pushed into `items` here. */
      contact={
        onCreateContact
          ? {
              email: row.contactEmail,
              canCreate: canCreateContact,
              /* On a row with no company the flow links one, which is triage. */
              canLinkCompany: ability.canTriage('b2b-proposal'),
              onCreate: onCreateContact,
              busy: busyKind === 'contact',
            }
          : null
      }
      items={items}
      disabled={anyBusy}
      /* Beside the menu that was used, never a panel-level banner. */
      failure={failure}
    />
  );
}
